import type {
  PostCompanyReviewExperiment2Input,
  PostCompanyReviewInput,
} from '@seek/ca-graphql-schema/types';
import { ORPHAN_REVIEW_COMPANY_ID } from '@seek/libs-shared';
import type { Language } from '@seek/melways-sites';

import type { ReviewPayload } from '../../type';

export const mapReview = (
  language: Language,
  review: ReviewPayload,
): PostCompanyReviewInput =>
  ({
    companyId: review.companyId ?? ORPHAN_REVIEW_COMPANY_ID,
    companyName: review.companyName,
    organisationId: review.organisationId,
    languageISO: language,
    details: {
      overallExperienceDetails: review.overallExperienceDetails,
      cons: review.theChallenges,
      pros: review.theGoodThings,
      isCompanyRecommended: review.isRecommended ?? false,
      overallRating: Number(review.overallRating),
      overallExperience: review.title,
    },
    job: {
      history: {
        from: {
          year: review.workHistoryFrom?.year,
          month: review.workHistoryFrom?.month,
        },
        ...(!review.isStillWorkHere && review.workHistoryTo
          ? {
              to: {
                year: review.workHistoryTo.year,
                month: review.workHistoryTo.month,
              },
            }
          : {}),
      },
      isJobTitleAnonymised: false,
      location: {
        fullLocation: review.fullLocation,
        locationId: review.locationId,
      },
      roleTitleId: review.jobTitleId,
      title: review.jobTitleText,
      workType: review.workType,
    },
    salary: {
      includeBonus: review.salary?.includeBonus ?? false,
      rating: review.salaryRating,
      type: review.salaryType,
      minimum: review.salary?.minimum,
      maximum: review.salary?.maximum,
      currency: review.salary?.currency,
    },
    ...(review.perksAndBenefitsRating
      ? {
          perksAndBenefits: { rating: Number(review.perksAndBenefitsRating) },
        }
      : {}),
    ...(review.careerOpportunityRating
      ? {
          careerOpportunity: {
            rating: Number(review.careerOpportunityRating),
          },
        }
      : {}),
    ...(review.diversityRating
      ? {
          diversity: {
            rating: Number(review.diversityRating),
          },
        }
      : {}),
    ...(review.executiveManagementRating
      ? {
          executiveManagement: {
            rating: Number(review.executiveManagementRating),
          },
        }
      : {}),
    ...(review.workEnvironmentRating
      ? {
          workEnvironment: {
            rating: Number(review.workEnvironmentRating),
          },
        }
      : {}),
    ...(review.workLifeBalanceRating
      ? {
          workLifeBalance: {
            rating: Number(review.workLifeBalanceRating),
          },
        }
      : {}),
  }) as PostCompanyReviewInput;

export const mapReviewExperimentTwo = (
  review: ReviewPayload,
): PostCompanyReviewExperiment2Input => {
  return {
    reviewId: review.reviewId || null, // Don't send empty string
    companyName: review.companyName ?? '',
    companyId: review.companyId ?? ORPHAN_REVIEW_COMPANY_ID,
    organisationId: review.organisationId || null, // Don't send empty string
    zone: review.zone,
    workLifeBalance: {
      rating: review.workLifeBalanceRating
        ? Number(review.workLifeBalanceRating)
        : null,
      description: review.workLifeBalanceDescription || '',
    },
    workEnvironment: {
      rating: review.workEnvironmentRating
        ? Number(review.workEnvironmentRating)
        : null,
      description: review.workEnvironmentDescription || '',
    },
    executiveManagement: {
      rating: review.executiveManagementRating
        ? Number(review.executiveManagementRating)
        : null,
      description: review.executiveManagementDescription || '',
    },
    diversity: {
      rating: review.diversityRating ? Number(review.diversityRating) : null,
      description: review.diversityDescription || '',
    },
    careerOpportunity: {
      rating: review.careerOpportunityRating
        ? Number(review.careerOpportunityRating)
        : null,
      description: review.careerOpportunityDescription || '',
    },
    perksAndBenefits: {
      rating: review.perksAndBenefitsRating
        ? Number(review.perksAndBenefitsRating)
        : null,
      description: review.perksAndBenefitsDescription || '',
    },
    salary: {
      rating: review.salaryRating || null,
      type: review.salaryType || null,
      minimum: review.salary?.minimum || null,
      maximum: review.salary?.maximum || null,
      currency: review.salary?.currency || null,
      includeBonus: review.salary?.includeBonus ?? null,
    },
    overall: {
      rating: Number(review.overallRating),
      description: review.overallDescription || '',
    },
  };
};
